import * as React from 'react'
import {
  Drawer, Box, Typography, IconButton, Button, TextField, MenuItem, Chip, Tooltip, Alert,
  FormControlLabel, Checkbox, InputAdornment, RadioGroup, Radio,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import AcUnitOutlinedIcon from '@mui/icons-material/AcUnitOutlined'
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined'
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined'

import {primaryBtnSx, secondaryBtnSx} from '../../../../styles/patterns'
import {FONT, SIZE, WEIGHT} from '../../../../styles/foundation/typography'
import {NAVY, SUBTLE, BORDER, PRIMARY_BLUE, WARNING, SUCCESS, withOpacity, ERROR} from '../../../../styles/foundation/colors'
import {GAP} from '../../../../styles/foundation/spacing'

import {kg, dims, grams, int} from '../format'
import {FREEZE_LEVEL, FREEZE_LEVEL_LABEL} from '../data/reference'
import {POLICY} from '../data/sopCopy'
import {
  DWS_SLOTS, DWS_VIDEO_TYPES, DWS_IMAGE_TYPES, DWS_MAX_VIDEO_BYTES, DWS_MAX_IMAGE_BYTES, mb,
} from '../data/dwsCopy'
import {chargeableKg, withinTolerance, getVolumetricDivisor} from '../weightMath'
import {PACK_KIND_LABEL, volumetric, packOptionLabel, brandingLabel} from '../packing/state/packingModel'
import {validateVideo, validateImage, buildEvidenceFiles, rawEvidenceFiles} from '../state/dwsUpload'
import {validateNewCode} from '../catalogue/FixCodeDrawer'
import {newSkuGapsFor, gapSentence, weightFacts, hasAttachedEvidence, DECLARATION, LEVELS} from './request'
import CartonSVG from '../components/CartonSVG'
import SleeveSVG from '../components/SleeveSVG'
import DropZone from '../components/DropZone'
import KeyValue from '../components/KeyValue'
import {chipSx} from '../components/tone'

/** The extensions, not the MIME types — two entries share 'JPG', so this dedupes. */
function formatWords(types) {
  const words = Array.from(new Set(Object.keys(types).map((t) => types[t])))
  return words.slice(0, -1).join(', ') + ' or ' + words[words.length - 1]
}

const VIDEO_FORMATS = formatWords(DWS_VIDEO_TYPES)
const IMAGE_FORMATS = formatWords(DWS_IMAGE_TYPES)

/**
 * `DWS_SLOTS` asks the fifth shot for 'AWB barcode in focus'. A product this
 * drawer has just described as never shipped has no AWB, so that one hint is
 * replaced — at the call site, per DropZone. The shared constant is left alone
 * because it also drives the DWS module's own copy, where the AWB is real.
 */
const LABEL_SLOT_KEY = 'lbl'
const LABEL_SLOT_HINT = 'The shipping label as it will be applied'

const labelSx = {fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE}
const sectionLabelSx = {fontFamily: FONT, fontWeight: WEIGHT.bold, fontSize: SIZE.xs, color: NAVY, mb: 0.75}
const alertSx = {fontFamily: FONT, fontSize: SIZE['2xs'], py: 0.75, '& .MuiAlert-message': {lineHeight: 1.55}}

// MUI draws the required asterisk in the label's own colour — the same grey
// as the label text, which reads as punctuation rather than a marker. Every
// gate in this drawer is a required field, so the marker has to be visible.
const requiredAsteriskSx = {'& .MuiFormLabel-asterisk': {color: ERROR}}

const fieldSx = {
  ...requiredAsteriskSx,
  fontFamily: FONT,
  fontSize: SIZE.xs,
  '& .MuiFormHelperText-root': {fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, ml: 0.25, mr: 0},
  // Project rule 16: MUI leaks theme.typography.body1's 16px into these two.
  // A shared constant cannot be edited to fix it, so it is beaten here, at the
  // call site, where it belongs. `IniRaiseDrawer`'s `fieldSx` now carries the
  // same pair, so the two drawers' fields render at one size.
  '& .MuiInputBase-input': {fontFamily: FONT, fontSize: SIZE.xs},
  '& .MuiSelect-select': {fontFamily: FONT, fontSize: SIZE.xs},
}

/**
 * A Select's menu is PORTALLED to document.body, so `fieldSx`'s descendant
 * selectors — which reach only the closed trigger — never touch it, and its
 * items keep theme.typography.body1's 1rem. Same shape and same SIZE['2xs'] as
 * IniFilterBar's and IniRaiseDrawer's menus.
 */
const selectMenuProps = {
  PaperProps: {sx: {'& .MuiMenuItem-root': {fontFamily: FONT, fontSize: SIZE['2xs']}}},
}

/**
 * Everything a REQUIRED Select in this drawer needs, in one place.
 *
 * `required` on a MUI Select reaches only the hidden native input — the one
 * carrying `aria-hidden` and `tabIndex={-1}` — while the control a screen
 * reader actually lands on is a `role="button"` div that gets no
 * `aria-required` at all. The asterisk in the label is then the only signal,
 * and a marker is not a state: the field announces as "Sub-category, button,
 * collapsed" while the text field beside it announces as required. Extra props
 * on `SelectProps` also land on that hidden input, so this goes through
 * `SelectDisplayProps`, which is the one bag MUI spreads onto the trigger.
 */
const requiredSelectProps = {
  MenuProps: selectMenuProps,
  SelectDisplayProps: {'aria-required': true},
}

/** The <legend> that has to name the RadioGroup inside it — see section 3. */
const LEVEL_LEGEND_ID = 'new-sku-freeze-level'

/**
 * Which of the two weights decides the bill, AND the figure it decides it at —
 * "governs" says nothing useful without the number the chargeable field is
 * expected to hold. Volumetric governing is the case worth colouring: the bill
 * is then being driven by the packing material rather than by the product, and
 * that is the one a merchant can act on by repacking.
 */
const GOVERNS_SENTENCE = {
  dead: (v) => `Dead weight governs at ${kg(v)}`,
  vol: (v) => `Volumetric weight governs at ${kg(v)}`,
  both: (v) => `Dead and volumetric are equal at ${kg(v)}`,
}

/**
 * A numbered step heading, the shape the Weight Dispute module's own evidence
 * flow uses. The number is decorative — the heading beside it already names the
 * step — so it is marked `aria-hidden` rather than read out twice.
 *
 * Copied from `IniRaiseDrawer` rather than lifted to a shared component: a lift
 * edits that 885-line file, which is outside this change's blast radius. Worth
 * lifting to `components/StepHead.js` as its own follow-up.
 */
function StepHead({n, title, chip}) {
  return (
    <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.75}}>
      <Box
        aria-hidden
        sx={{
          width: 20, height: 20, flexShrink: 0, borderRadius: '6px',
          bgcolor: withOpacity(PRIMARY_BLUE, 0.12), color: PRIMARY_BLUE,
          display: 'grid', placeItems: 'center',
          fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.bold,
        }}
      >
        {n}
      </Box>
      <Typography sx={{fontFamily: FONT, fontWeight: WEIGHT.bold, fontSize: SIZE.xs, color: NAVY}}>
        {title}
      </Typography>
      {chip && <Chip size='small' label={chip} sx={chipSx('ok')} />}
    </Box>
  )
}

/**
 * Weight Freeze (v2) — Initiate: freezing a product that has never shipped.
 *
 * `IniRaiseDrawer` raises a freeze over rows drawn from shipment history. A
 * brand-new merchant has none, so that drawer's candidate table is empty and
 * there is no route to a freeze at all. This is that route: the merchant
 * DECLARES the parcel — its dimensions, its dead weight, its packing material —
 * and the arithmetic follows from what they typed.
 *
 * SIBLING, NOT A MODE. It reuses the raise drawer's primitives on purpose (the
 * numbered steps, the drop zones, the five named shot slots, the carton
 * drawing, the declaration, the footer gate) so it reads as the same drawer,
 * but it holds none of that drawer's history machinery.
 *
 * SECTION ORDER: the product, then the level, then the material, then the
 * parcel, then the promise, then the proof — the same order `IniRaiseDrawer`
 * puts the sections it shares with this one in, so the two read as one drawer.
 * The material sits above every figure that depends on it, because each of
 * those is a figure about a parcel made of that material, and the declaration
 * sits after all three weights because it is what confirms them.
 *
 * THE LEVEL IS CHOOSABLE, and Sub-category is the default. A product that is
 * not in the catalogue yet still belongs to a sub-category — the merchant is
 * telling us which one, the same way they are telling us the weight — and this
 * module's stated business default is that a freeze is agreed for a group of
 * similar products rather than one code at a time. There is no shipment row
 * here to infer the sub-category from, so the options are the CATALOGUE's own
 * (the `subcats` map, flattened), where the raise drawer reads the ticked rows.
 * `handleSubmit` writes whichever level is selected, and the header and the
 * footer follow the radio rather than stating a level of their own.
 *
 * NO FIGURE IS PRE-FILLED, and that is the whole point. The level radio starts
 * on the recommendation and the sub-category select starts empty; past that
 * there is no proposal to derive, no most-probable weight, no covered/blocked
 * partition, no DWS records to tick and no packing material pre-selected — a
 * product with no history has none of it, and a "0 of 0" partition would read
 * as "all rows ready", the opposite of the truth.
 *
 * DERIVED, NEVER TYPED: volumetric comes from the typed dimensions and
 * chargeable from the higher of dead and volumetric, both live as the merchant
 * types. Holding either in state would let the stored number drift from the
 * arithmetic on screen.
 *
 * MOUNT LIFECYCLE — a hard requirement on the caller. `InitiatePage` must
 * render `{newSkuOpen && <IniNewSkuDrawer open={newSkuOpen} … />}`. The raise
 * drawer gets a fresh mount for free because it is co-conditioned on a
 * non-empty selection; this one has no such co-condition, and mounting it
 * permanently while toggling `open` would carry a cancelled draft — including
 * its blob urls — into the next visit.
 *
 * WIDTH: the raise drawer's, to the pixel. Drawers are portalled, so they sit
 * outside the page shell's `zoom: 0.8`; 820 is what the five photo slots need
 * three across.
 */
export default function IniNewSkuDrawer({open, packs, skus, subcats, onClose, onSubmit}) {
  const [product, setProduct] = React.useState('')
  const [code, setCode] = React.useState('')
  // The recommendation, pre-selected — see THE LEVEL IS CHOOSABLE above. It is
  // the one answer this drawer offers before the merchant types anything, and
  // it is the same default the raise drawer opens on.
  const [level, setLevel] = React.useState(FREEZE_LEVEL.SUBCAT)
  const [subcat, setSubcat] = React.useState('')
  const [len, setLen] = React.useState('')
  const [bre, setBre] = React.useState('')
  const [hgt, setHgt] = React.useState('')
  const [dead, setDead] = React.useState('')
  const [packId, setPackId] = React.useState('')
  const [declared, setDeclared] = React.useState(false)
  const [remarks, setRemarks] = React.useState('')
  const [video, setVideo] = React.useState(null)
  const [images, setImages] = React.useState({})
  const [fileErrors, setFileErrors] = React.useState({})
  const [dragKey, setDragKey] = React.useState(null)

  // Tracked in a ref rather than in state, so the unmount cleanup still sees
  // every url this drawer ever minted even if the last render had dropped it.
  const blobUrls = React.useRef([])
  const trackUrl = (file) => {
    const url = URL.createObjectURL(file)
    blobUrls.current.push(url)
    return url
  }
  const releaseUrl = (url) => {
    if (!url) return
    URL.revokeObjectURL(url)
    blobUrls.current = blobUrls.current.filter((u) => u !== url)
  }
  React.useEffect(() => () => {
    blobUrls.current.forEach((u) => URL.revokeObjectURL(u))
    blobUrls.current = []
  }, [])

  // The module's ONE code guard, imported rather than re-derived, so this
  // drawer and Catalogue's fix-code flow can never disagree about what a
  // usable code is. `skus` is read for this and for nothing else — no
  // shipment history enters this drawer.
  const codeCheck = validateNewCode(code, skus)

  // `subcats` is the catalogue's {category: [sub, …]} MAP, and what the select
  // needs is the flat list of sub-category names — the category above them is
  // not what a freeze is granted at. Through a Set, the way the raise drawer
  // builds its own list, so two categories that ever come to share a
  // sub-category name offer it once; sorted, so both drawers show one order.
  const subOptions = React.useMemo(() => {
    const set = new Set()
    Object.keys(subcats || {}).forEach((cat) => (subcats[cat] || []).forEach((s) => set.add(s)))
    return Array.from(set).sort()
  }, [subcats])

  /** '' and a half-typed '-' both read as "not entered yet", never as a measurement of zero. */
  const num = (v) => Number(v) || 0
  const dimsTriple = [num(len), num(bre), num(hgt)]

  // packingModel's `volumetric`, NOT weightMath's `volumetricKg`: this one
  // returns null until all three sides are above zero, so a half-typed box
  // shows no figure instead of a false 0.00 kg. It is the module's single
  // definition of a usable triple, and Packing's own panels gate on it too.
  const volKg = volumetric(dimsTriple)
  const DIMENSION_FIELDS = [
    ['Length', len, setLen],
    ['Breadth', bre, setBre],
    ['Height', hgt, setHgt],
  ]
  const deadKg = num(dead)
  const chargeKg = volKg == null ? null : chargeableKg(deadKg, volKg)

  // `chargeKg` IS the derived figure, so `facts.mismatch` can never fire; only
  // `.governs` and `.higher` are read. Same function the raise drawer uses, so
  // the governs sentence is one sentence in the codebase, not two.
  const facts = volKg == null ? null : weightFacts(deadKg, volKg, chargeKg)

  const pack = packs.find((p) => p.id === packId) || null

  // Shaped ONCE per render and reused by both the submit gate and the payload
  // `handleSubmit` hands up, so the set the gate opened on can never be a
  // different set from the one the request carries. `hasAttachedEvidence` reads
  // this immutable `{video, images[]}` shape, never the live `images` state.
  const files = buildEvidenceFiles({video, images})
  const attachedEvidence = hasAttachedEvidence(files)

  const gaps = newSkuGapsFor({
    product, code, clash: codeCheck.clash, level, subcat, volKg, deadKg, packId, declared,
    attachedEvidence,
  })
  const sentence = gapSentence(gaps)

  // What the request REACHES, and the noun the reviewer's own screens print for
  // it. At Sub-category level it is the sub-category; at SKU level the code the
  // merchant typed. `subjectFor` is not called: it answers from `covered`, the
  // ticked rows, and this drawer has none — it would return '' at SKU level for
  // every product on earth.
  const subject = level === FREEZE_LEVEL.SUBCAT ? subcat : codeCheck.trimmed

  // Through the label map, like every other screen in the module, never the raw
  // enum. For these two levels the stored value and the printed word are the
  // same string, so interpolating `level` reads correctly today — and prints
  // the wrong noun the day 'Combo' (label: 'Combination') is offered.
  const levelLabel = FREEZE_LEVEL_LABEL[level] || level

  // How far a Sub-category freeze actually reaches, counted off the live
  // catalogue. Without it the merchant is asked to agree to a group-wide freeze
  // on a group whose size nothing on this drawer has told them; with it the
  // default level states its own consequence. Zero is a real and different
  // answer — a brand-new sub-category — so it gets its own sentence.
  const reachCount = React.useMemo(
    () => (subcat ? (skus || []).filter((s) => s.sub === subcat).length : 0),
    [skus, subcat],
  )

  // The footer's promise, which has to match the payload it describes. At SKU
  // level `covers` IS the subject, so "covering" is literally true. At
  // Sub-category level the two are different facts — the request reaches the
  // whole sub-category, while `covers` records the one code being introduced —
  // and spending the verb "cover" on the reach would put the sentence the
  // merchant agreed to at odds with the field a reviewer's screen reads.
  const promise = level === FREEZE_LEVEL.SUBCAT
    ? `Goes to Prozo for review as ${levelLabel} level, reaching all of ${subject} — declared for ${codeCheck.trimmed}.`
    : `Goes to Prozo for review as ${levelLabel} level, covering ${subject}.`

  // Named `takePackDims`, not `usePackDims`: a `use`-prefixed function called
  // from an onClick trips react-hooks/rules-of-hooks.
  const takePackDims = () => {
    const [l, b, h] = pack.dims
    setLen(String(l)); setBre(String(b)); setHgt(String(h))
  }

  // Validation is `state/dwsUpload.js`'s own — the same format and size rules
  // the Evidence Studio applies, so a clip this drawer accepts is a clip that
  // one would have accepted too.
  const setFileError = (key, message) => setFileErrors((prev) => {
    const next = {...prev}
    if (message) next[key] = message; else delete next[key]
    return next
  })

  const takeVideo = (file) => {
    const check = validateVideo(file)
    if (!check.ok) {
      setFileError('video', check.message)
      return
    }
    setFileError('video', '')
    setVideo((prev) => {
      if (prev) releaseUrl(prev.url)
      return {name: file.name, size: file.size, url: trackUrl(file), file}
    })
  }

  const clearVideo = () => setVideo((prev) => {
    if (prev) releaseUrl(prev.url)
    return null
  })

  // Replacing a file in a slot has to revoke the url the old one held, or that
  // blob stays alive for the life of the tab.
  const takeImage = (slotKey, file) => {
    const check = validateImage(file)
    if (!check.ok) {
      setFileError(slotKey, check.message)
      return
    }
    setFileError(slotKey, '')
    setImages((prev) => {
      if (prev[slotKey]) releaseUrl(prev[slotKey].url)
      return {...prev, [slotKey]: {name: file.name, size: file.size, url: trackUrl(file), file}}
    })
  }

  const clearImage = (slotKey) => setImages((prev) => {
    if (!prev[slotKey]) return prev
    releaseUrl(prev[slotKey].url)
    const next = {...prev}
    delete next[slotKey]
    return next
  })

  const pickFrom = (event) => {
    const file = event.target.files && event.target.files[0]
    event.target.value = ''
    return file || null
  }

  // One `dragKey` for all six zones, keyed-compared: that is what stops the
  // pointer crossing from one slot into its neighbour leaving both lit.
  const dragOver = (key) => (event) => { event.preventDefault(); setDragKey(key) }
  const dragLeave = (key) => (event) => {
    event.preventDefault()
    setDragKey((k) => (k === key ? null : k))
  }
  const dropOn = (key, take) => (event) => {
    event.preventDefault()
    setDragKey(null)
    const file = event.dataTransfer.files && event.dataTransfer.files[0]
    if (file) take(file)
  }

  function handleSubmit() {
    // Re-checked here, not just at the disabled-button boundary: the button
    // can be reached by keyboard while state is mid-update.
    if (gaps.length) return

    // The urls now belong to the request the store is about to hold, so stop
    // tracking them here — this drawer unmounts the moment `InitiatePage`
    // clears `newSkuOpen`, and its cleanup would otherwise revoke urls the
    // request still points at. `files` is the set shaped for this render, the
    // same one the gate above opened on.
    const handedOn = [files.video && files.video.url].concat(files.images.map((f) => f.url)).filter(Boolean)
    blobUrls.current = blobUrls.current.filter((u) => !handedOn.includes(u))

    onSubmit({
      level,
      subject,
      // Keyed `product` to match the catalogue record's own field name, so the
      // request and the catalogue never hold the same fact under two spellings.
      // It is NOT a whole record: there is no `cat` here, no `sub` at SKU
      // level, and the code lives in `covers[0]` rather than under `sku`.
      // Minting a catalogue row from this request would have to ask for those
      // first — filed as a follow-up, not implied by this key.
      product: product.trim(),
      deadKg,
      volKg,
      chargeKg,
      packId,
      // The one SKU the merchant declared, and the only one known to exist —
      // at BOTH levels. A SKU row's key IS its code, so this is the identical
      // shape a one-row SKU-level raise produces, not a fabricated key.
      //
      // At Sub-category level the two fields say different things and are both
      // true: the request's REACH is the sub-category named by `subject`, and
      // it will govern whatever else lands in that sub-category later, while
      // `covers` records the single SKU being introduced with it. Listing the
      // sub-category's existing SKUs here instead would claim this request was
      // raised over rows the merchant never saw and never described.
      covers: [codeCheck.trimmed],
      // Zero DWS records exist for a product that has never shipped. Counted
      // apart from `evidenceFiles` so `raisedWith` cannot claim scanner records
      // that do not exist — see ./request.
      evidence: 0,
      evidenceAwbs: [],
      evidenceFiles: files,
      // The File objects themselves, for the live upload (data/api.js#apiUploadEvidence). Never
      // stored: `evidenceFiles` above is what a request keeps, this rides only to the submit handler.
      evidenceRaw: rawEvidenceFiles({video, images}),
      remarks,
      // The provenance of `volKg`: without it a reviewer sees a volumetric that
      // can contradict the declared pack's own, with nothing on the request
      // saying where it came from. Documented on `buildRequest` in ./request.
      declaredDims: dimsTriple,
    })
  }

  return (
    <Drawer anchor='right' open={open} onClose={onClose} sx={{'& .MuiDrawer-paper': {width: {xs: '100%', sm: 660, md: 820}, maxWidth: '100%'}}}>
      <Box sx={{display: 'flex', flexDirection: 'column', height: '100%'}}>
        <Box sx={{p: 2, borderBottom: `1px solid ${BORDER}`, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1}}>
          <Box sx={{minWidth: 0}}>
            <Typography sx={{fontFamily: FONT, fontWeight: WEIGHT.bold, fontSize: SIZE.md, color: NAVY}}>
              Freeze weight for a new product
            </Typography>
            {/* Follows the radio below rather than stating a level of its own:
                the merchant chooses it here (see THE LEVEL IS CHOOSABLE above),
                and a subtitle that disagreed with the choice would be the first
                thing they read. Where the raise drawer's subtitle counts the
                rows selected, this one has no rows to count, so the level is
                what it has to say. */}
            <Typography sx={labelSx}>
              {levelLabel} level
            </Typography>
          </Box>
          <IconButton onClick={onClose} aria-label='Close, without raising a request' size='small'>
            <CloseIcon fontSize='small' />
          </IconButton>
        </Box>

        <Box sx={{p: 2, overflowY: 'auto', flex: 1, display: 'grid', gap: GAP.lg}}>
          {/* 1. Packing-material warning — non-dismissible, permanent. */}
          <Alert severity='warning' sx={alertSx}>
            Every weight freeze is conditional on the declared packing material being used. Change
            the material and the freeze does not hold.
          </Alert>

          {/* 2. The product. */}
          <Box>
            <Typography sx={{...sectionLabelSx, mb: 1.25}}>The product</Typography>
            {/* BOTH halves, both required. Every catalogue record carries a
                name and a code — see `product` on the payload — and a reviewer
                looking at this request has no shipment row to read the name off
                the way they can for a raise. The name comes first because it is
                what a person recognises the product by; the code is what the
                system will.

                The "must not already be in the catalogue" rule is stated ONCE,
                by the code field's own helper — which also carries the routing
                instruction, where an existing product gets frozen instead. The
                footer says only that something is missing (see `gapSentence`),
                so deleting that helper would leave a merchant who typed an
                existing code with a shut button and no reason given. */}
            <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, 1fr)'}, gap: 1.5}}>
              <TextField
                required
                label='Product name' size='small' value={product}
                helperText='The listing name for this exact code, size and colour included.'
                onChange={(event) => setProduct(event.target.value)}
                sx={fieldSx}
              />
              <TextField
                required
                label='SKU code' size='small' value={code}
                error={codeCheck.clash}
                helperText={codeCheck.clash
                  ? 'That code is already in the catalogue. Freeze it from the table behind this drawer.'
                  : 'At least 2 characters. Exactly as it will appear on the shipping label.'}
                onChange={(event) => setCode(event.target.value)}
                sx={fieldSx}
              />
            </Box>
            {/* A merchant who has seen the raise drawer will look for the
                shipment table, the most-probable line and the DWS list. This
                says in one sentence why they are gone, which is the honest
                alternative to rendering them as zeros. It no longer repeats
                what the freeze is granted against — the warning strip at the
                top of the drawer and the packing-material helper both say it. */}
            <Alert severity='info' sx={{...alertSx, mt: 1.5}}>
              This product has never shipped, so no figure below is pre-filled — every one of them is one you declare.
            </Alert>
          </Box>

          {/* 3. Freeze at which level. The raise drawer's fieldset, option for
              option and word for word — the same decision, argued the same way
              in both drawers. What differs below it is this flow's own: the
              sub-category options come from the catalogue rather than from
              ticked rows, neither of that drawer's two alerts is copied, and
              the helper text is its own (see the section comment there).

              The aria wiring here is NOT copied. MUI's `RadioGroup` renders its
              own `role="radiogroup"` inside the fieldset, and a <legend> names
              the fieldset, not that inner element — VoiceOver reports the
              nearest owner and announces an unnamed group. `aria-labelledby`
              closes that, and each option's why-line is tied to its own radio
              with `aria-describedby`, because at Sub-category level that
              sentence is the only text on screen explaining what the
              pre-selected default reaches. The raise drawer wants the same
              two props; it is outside this change and is filed as a
              follow-up. */}
          <Box component='fieldset' sx={{border: `1px solid ${BORDER}`, borderRadius: '10px', p: 1.5, m: 0}}>
            <Typography component='legend' id={LEVEL_LEGEND_ID} sx={sectionLabelSx}>Freeze at which level</Typography>
            <RadioGroup
              aria-labelledby={LEVEL_LEGEND_ID}
              value={level}
              onChange={(event) => setLevel(event.target.value)}
              sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, 1fr)'}, gap: 1}}
            >
              {LEVELS.map((lv) => {
                const label = lv.l + (lv.v === FREEZE_LEVEL.SUBCAT ? ' (recommended)' : '')
                const whyId = `${LEVEL_LEGEND_ID}-why-${lv.v}`
                return (
                  <Box
                    key={lv.v}
                    sx={{border: `1px solid ${level === lv.v ? PRIMARY_BLUE : BORDER}`, borderRadius: '8px', p: 1}}
                  >
                    <FormControlLabel
                      value={lv.v}
                      control={<Radio size='small' inputProps={{'aria-describedby': whyId}} />}
                      label={<Typography sx={{fontFamily: FONT, fontWeight: WEIGHT.semibold, fontSize: SIZE.xs, color: NAVY}}>{label}</Typography>}
                      sx={{m: 0, alignItems: 'flex-start'}}
                    />
                    <Typography id={whyId} sx={{...labelSx, ml: 3.5, display: 'block'}}>{lv.why}</Typography>
                  </Box>
                )
              })}
            </RadioGroup>
          </Box>

          {/* 4. Sub-category (Sub-category level only). The options are the
              catalogue's, not a selection's: there are no ticked rows behind
              this drawer to read sub-categories off, which is also why neither
              of the raise drawer's two alerts is copied here — one counts the
              sub-categories a selection spans, and the other warns about
              'Uncategorised', a placeholder `subOf` invents for a row with no
              sub-category. Neither can arise from a fixed list of real ones. */}
          {level === FREEZE_LEVEL.SUBCAT && (
            <Box>
              <TextField
                select size='small' label='Sub-category' value={subcat} required
                helperText={subOptions.length === 0
                  ? 'No sub-categories are on file for this account yet. Choose SKU level above to freeze this code on its own.'
                  : 'Pick the sub-category this product belongs to.'}
                onChange={(event) => setSubcat(event.target.value)}
                sx={{...fieldSx, maxWidth: 320}}
                SelectProps={requiredSelectProps}
              >
                {/* An account whose catalogue holds no sub-category at all can
                    reach this: the store deletes a category once its last
                    sub-category moves away. The default level would then be
                    unsatisfiable behind an empty popover and one generic
                    footer line, with the way out — the SKU radio above — never
                    mentioned. A disabled option says the list is empty rather
                    than broken; the helper text says what to do instead. */}
                {subOptions.length === 0
                  ? <MenuItem disabled value=''>No sub-categories on file</MenuItem>
                  : subOptions.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </TextField>
              {/* What the merchant is actually agreeing to. Every figure below
                  is declared for ONE product, and at this level the freeze
                  governs the whole sub-category on them — so the size of that
                  group is not a detail to leave to the reviewer's screens,
                  which never print it either. */}
              {subcat && (
                <Typography sx={{...labelSx, mt: 0.75, display: 'block'}}>
                  {reachCount > 0
                    ? `${int(reachCount)} ${reachCount === 1 ? 'product' : 'products'} in the catalogue already `
                      + `${reachCount === 1 ? 'sits' : 'sit'} in ${subcat}. The freeze reaches all of them, at the `
                      + 'weights declared below.'
                    : `Nothing in the catalogue sits in ${subcat} yet. The freeze reaches whatever is added to it `
                      + 'later, at the weights declared below.'}
                </Typography>
              )}
            </Box>
          )}

          {/* 5. Packing material — the choice, and what the catalogue holds
              against it. Before the weights, because the material is the
              condition the whole freeze hangs on and because the box the
              merchant measures below is the box this material makes. */}
          <Box>
            <Typography sx={sectionLabelSx}>Packing material</Typography>
            <TextField
              select size='small' label='Packing material this product will ship in' value={packId} required
              helperText='The freeze is granted against this material and nothing else.'
              onChange={(event) => setPackId(event.target.value)}
              sx={{...fieldSx, minWidth: 340}}
              SelectProps={requiredSelectProps}
            >
              {/* Branding rides on the option, not just on the card below: two
                  materials can share a shape and differ only in whether they
                  carry the merchant's own print. */}
              {packs.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {packOptionLabel(p)}
                </MenuItem>
              ))}
            </TextField>

            {/* Only what is the MATERIAL's own and nothing else's. This card
                used to carry an outline of the material's box, then — after the
                outline went — its dimension triple and its volumetric weight,
                which were still a second parcel measured and weighed in a drawer
                whose whole subject is the one the merchant is describing below.
                Where the two genuinely disagree the alert under the dimensions
                says so in full, which is the moment that comparison is worth
                making. */}
            {pack && (
              <Box sx={{border: `1px solid ${BORDER}`, borderRadius: '10px', p: 1.5, mt: 1.5, maxWidth: {sm: 420}}}>
                <KeyValue items={[
                  {label: 'Type', value: PACK_KIND_LABEL[pack.kind] || pack.kind},
                  {label: 'Branding', value: brandingLabel(pack.branding)},
                  {label: 'Self weight', value: grams(pack.selfWeightG)},
                ]}
                />
              </Box>
            )}
          </Box>

          {/* 6. Product weight and dimensions, in the order the figures depend
              on each other: the product's own dead weight, then the box it
              ships in, then the two the courier derives from those.

              THE DEAD WEIGHT IS THE PRODUCT, NOT THE PACKED PARCEL. `deadKg`
              means the product's own weight everywhere else in the module — the
              raise drawer's field is anchored to the SKU, and Packing adds the
              material's self weight on top of it — and this field lands in the
              SAME request field the raise drawer's does. Asking for the packed
              figure here would put two different quantities in one column.

              Volumetric and chargeable are READ-ONLY because they are
              arithmetic. Holding either in state, or offering either as a
              field, would let a merchant propose a chargeable weight their own
              box contradicts. */}
          <Box>
            <Typography sx={{...sectionLabelSx, mb: 1.25}}>Product weight and dimensions</Typography>

            <Box sx={{
              display: 'grid',
              gridTemplateColumns: {xs: '1fr', sm: 'auto minmax(0, 1fr)'},
              gap: 2,
              alignItems: 'start',
            }}
            >
              {/* The parcel these figures describe, drawn from the triple beside
                  it, so correcting a side redraws it. A box that is not yet a
                  usable triple gets the sleeve rather than an empty slot that
                  appears from nowhere on the third keystroke. */}
              <Box sx={{flexShrink: 0, width: 96, textAlign: 'center'}}>
                {volKg == null ? <SleeveSVG size={96} /> : <CartonSVG dims={dimsTriple} size={96} />}
                <Typography sx={{...labelSx, display: 'block', mt: 0.25}}>Not to scale</Typography>
              </Box>

              <Box sx={{display: 'grid', gap: 1.5, minWidth: 0}}>
                {/* `required` on all four: they are what the freeze is granted
                    at, and a field that blocks the ask has to say so here rather
                    than only in the footer. MUI appends " *" to the accessible
                    name it derives from `label`, so a test querying these by
                    exact label has to match the asterisk too. */}
                {/* The units are end adornments rather than label text, the
                    same as the raise drawer's four: at the width four fields
                    leave them, '(cm)' is what a truncated label drops first. */}
                <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', sm: 'repeat(4, 1fr)'}, gap: 1.5}}>
                  <TextField
                    required
                    label='Dead weight' size='small' type='number' value={dead}
                    helperText='The product on a scale, on its own — not the packed parcel.'
                    onChange={(event) => setDead(event.target.value)} inputProps={{step: 0.05, min: 0}}
                    InputProps={{endAdornment: (
                      <InputAdornment position='end'><Typography sx={labelSx}>kg</Typography></InputAdornment>
                    )}}
                    sx={fieldSx}
                  />
                  {DIMENSION_FIELDS.map(([label, value, set]) => (
                    <TextField
                      key={label}
                      required
                      label={label} size='small' type='number' value={value}
                      onChange={(event) => set(event.target.value)} inputProps={{step: 1, min: 0}}
                      InputProps={{endAdornment: (
                        <InputAdornment position='end'><Typography sx={labelSx}>cm</Typography></InputAdornment>
                      )}}
                      sx={fieldSx}
                    />
                  ))}
                </Box>
                <Typography sx={{...labelSx, display: 'block'}}>
                  Measure the outside of the box as it ships, packing material included.
                </Typography>

                {/* The two the courier works out, stated as results rather than
                    fields. 'Not yet' rather than 0.00 kg while the box is
                    incomplete: a zero here would read as a measurement. A
                    volumetric of exactly 0 — any parcel under 25 cm³ — is a real
                    measurement, so the test is `volKg == null`, never falsiness. */}
                <Box sx={{
                  display: 'grid', gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, 1fr)'}, gap: 1.5,
                  p: 1.25, borderRadius: '10px', bgcolor: withOpacity(NAVY, 0.03),
                }}
                >
                  <Typography sx={labelSx}>
                    Volumetric weight{' '}
                    <Box component='span' sx={{color: NAVY, fontWeight: WEIGHT.semibold}}>
                      {volKg == null ? 'Not yet' : kg(volKg)}
                    </Box>
                    <Box component='span' sx={{display: 'block'}}>
                      Length × breadth × height ÷ {int(getVolumetricDivisor())}.
                    </Box>
                  </Typography>
                  <Typography sx={labelSx}>
                    Chargeable weight{' '}
                    <Box component='span' sx={{color: NAVY, fontWeight: WEIGHT.semibold}}>
                      {chargeKg == null ? 'Not yet' : kg(chargeKg)}
                    </Box>
                    <Box component='span' sx={{display: 'block'}}>
                      The higher of dead and volumetric. What the freeze would bill at.
                    </Box>
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* `deadKg > 0` is load-bearing: without it an untouched form would
                state that dead and volumetric are equal at 0.00 kg, which is a
                claim about a parcel nobody has measured. */}
            {facts && deadKg > 0 && (
              <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 1}}>
                <Chip
                  size='small'
                  label={GOVERNS_SENTENCE[facts.governs](facts.higher)}
                  // The app theme title-cases chip labels — on the inner span,
                  // so the override has to reach it there — and that turns this
                  // sentence's unit into 'Kg'. It is a sentence carrying a
                  // measurement, not a status word.
                  sx={{
                    ...chipSx(facts.governs === 'vol' ? 'warn' : 'info'),
                    '& .MuiChip-label': {textTransform: 'none'},
                  }}
                />
              </Box>
            )}

            {/* Says out loud that the material declares no box of its own, and
                therefore where the volumetric figure comes from. The pack card
                above no longer carries the two rows that used to say the first
                half, so this line says both — the same sentence the raise
                drawer prints for a boxless material. */}
            {pack && pack.volKg == null && (
              <Typography sx={{...labelSx, display: 'block', mt: 1.5}}>
                {pack.label} declares no box of its own, so the volumetric weight here comes only from the
                dimensions you enter.
              </Typography>
            )}

            {/* The module anchors volumetric to the packing material; this
                drawer anchors it to what the merchant typed. When the two
                disagree beyond `withinTolerance` neither is silently preferred
                — the contradiction is stated, with one click to resolve it.
                Beside the dimensions rather than beside the material picker,
                because the dimensions are what the button rewrites. */}
            {pack && pack.volKg != null && volKg != null && !withinTolerance(volKg, pack.volKg) && (
              <Alert
                severity='warning'
                sx={{...alertSx, mt: 1.5}}
                action={pack.dims && (
                  <Button size='small' sx={secondaryBtnSx} onClick={takePackDims}>
                    Use {dims(pack.dims)}
                  </Button>
                )}
              >
                {pack.label} measures {dims(pack.dims)}, a volumetric weight of {kg(pack.volKg)}. You
                described {kg(volKg)}. Change the dimensions or the material — the freeze is argued on the
                dimensions you entered.
              </Alert>
            )}
          </Box>

          {/* 7. The declaration confirms every figure above it — the material,
              the product weight and the box — so it sits after all of them
              rather than inside the packing section, which was only the first of
              them. Boxed and tinted on tick, because this is a required gate
              rather than a preference: an unticked checkbox in a long form is
              easy to read past. */}
          <Box
            sx={{
              px: 1.5, py: 0.5, borderRadius: '10px',
              border: `1px solid ${declared ? SUCCESS : BORDER}`,
              bgcolor: declared ? withOpacity(SUCCESS, 0.08) : 'transparent',
            }}
          >
            <FormControlLabel
              sx={{display: 'flex', alignItems: 'flex-start', m: 0}}
              control={<Checkbox checked={declared} onChange={(event) => setDeclared(event.target.checked)} />}
              label={<Typography sx={{...labelSx, mt: 0.9}}>{DECLARATION}</Typography>}
            />
          </Box>

          {/* 8. Add evidence. Required here, unlike the raise drawer: with no
              courier scan and no billing history behind this product, the
              merchant's own readings are the whole of the evidence the request
              will ever carry. */}
          <Box>
            <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1}}>
              <Typography sx={sectionLabelSx}>Add evidence</Typography>
              <Chip
                size='small'
                label={attachedEvidence ? 'Attached — enough to submit' : 'Required: this product has never shipped'}
                // Rule 14 again: both labels are sentences, not status words.
                sx={{
                  ...chipSx(attachedEvidence ? 'ok' : 'danger'),
                  '& .MuiChip-label': {textTransform: 'none'},
                }}
              />
            </Box>
            {/* Why the readings matter — that this product has no courier scan
                behind them — is the chip above, so it is not said twice. The
                gate sentence stays, and only while the gate is open: once
                something is attached the chip says so and the line would be
                describing a state already on screen. */}
            <Typography sx={{...labelSx, mb: 1.5, display: 'block'}}>
              {'Readings you record here travel with the request.'
                + (attachedEvidence ? '' : ' One packing video, or one photo, is enough to submit.')}
            </Typography>

            <Box sx={{display: 'grid', gap: 2}}>
              <Box>
                <StepHead n={1} title='Packing video' chip='Recommended' />
                <DropZone
                  icon={VideocamOutlinedIcon}
                  big
                  label='Add a packing video'
                  hint={`One unbroken clip, ending on the scale. ${VIDEO_FORMATS} · up to ${mb(DWS_MAX_VIDEO_BYTES)}.`}
                  file={video}
                  error={fileErrors.video}
                  dragActive={dragKey === 'video'}
                  accept={Object.keys(DWS_VIDEO_TYPES).join(',')}
                  ariaLabel='Upload the packing video'
                  removeAriaLabel='Remove the packing video'
                  onPick={(event) => { const f = pickFrom(event); if (f) takeVideo(f) }}
                  onClear={clearVideo}
                  onDragOver={dragOver('video')}
                  onDragLeave={dragLeave('video')}
                  onDrop={dropOn('video', takeVideo)}
                />
              </Box>

              <Box>
                <StepHead n={2} title='Photo evidence' />
                <Typography sx={{...labelSx, mb: 1}}>
                  Five shots, one fact each. Keep the measuring device and the parcel in the same frame.{' '}
                  {IMAGE_FORMATS} · up to {mb(DWS_MAX_IMAGE_BYTES)} each.
                </Typography>
                <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)'}, gap: 1}}>
                  {DWS_SLOTS.map((slot) => (
                    <DropZone
                      key={slot.k}
                      icon={ImageOutlinedIcon}
                      label={slot.label}
                      // One slot's hint is this drawer's own — see LABEL_SLOT_HINT.
                      hint={slot.k === LABEL_SLOT_KEY ? LABEL_SLOT_HINT : slot.hint}
                      file={images[slot.k]}
                      error={fileErrors[slot.k]}
                      dragActive={dragKey === `img:${slot.k}`}
                      accept={Object.keys(DWS_IMAGE_TYPES).join(',')}
                      ariaLabel={`Upload the ${slot.label.toLowerCase()} image`}
                      removeAriaLabel={`Remove the ${slot.label.toLowerCase()} image`}
                      onPick={(event) => { const f = pickFrom(event); if (f) takeImage(slot.k, f) }}
                      onClear={() => clearImage(slot.k)}
                      onDragOver={dragOver(`img:${slot.k}`)}
                      onDragLeave={dragLeave(`img:${slot.k}`)}
                      onDrop={dropOn(`img:${slot.k}`, (f) => takeImage(slot.k, f))}
                    />
                  ))}
                </Box>
              </Box>
            </Box>
          </Box>

          {/* 9. Remarks. */}
          <TextField
            label='Add a note for the reviewer (optional)' size='small' multiline minRows={3} fullWidth
            helperText='A packing change, a seasonal variant, a known outlier.'
            value={remarks} onChange={(event) => setRemarks(event.target.value)}
            sx={fieldSx}
          />

          {/* 10. Closing note. The sentence before it is the standing rule that a
              raise is only a proposal; the sentence itself is POLICY.preliminary,
              verbatim from the source document rather than paraphrased here. The
              packing-material condition was here too, word for word from the
              warning strip at the top of the drawer, and is now said once. */}
          <Alert severity='info' sx={alertSx}>
            A merchant-raised freeze takes effect only after Prozo approves it. {POLICY.preliminary}
          </Alert>
        </Box>

        <Box sx={{p: 1.5, borderTop: `1px solid ${BORDER}`, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap'}}>
          <Typography sx={{...labelSx, flex: '1 1 260px', color: gaps.length ? WARNING : SUBTLE}}>
            {/* Built above, next to the `subject` it has to agree with. */}
            {gaps.length ? sentence : promise}
          </Typography>
          <Button sx={secondaryBtnSx} onClick={onClose}>Cancel</Button>
          {gaps.length ? (
            /* A disabled MUI Button fires no pointer events, so a bare Tooltip
               never opens and a keyboard user gets nothing. The focusable
               wrapper carries the same sentence the tooltip and the footer
               show — one string, three surfaces. */
            <Tooltip title={sentence}>
              <Box component='span' tabIndex={0} role='note' aria-label={'Submit is unavailable. ' + sentence}>
                <Button variant='contained' disabled sx={primaryBtnSx} startIcon={<AcUnitOutlinedIcon sx={{fontSize: 15}} />}>
                  Submit for Prozo review
                </Button>
              </Box>
            </Tooltip>
          ) : (
            <Button variant='contained' sx={primaryBtnSx} startIcon={<AcUnitOutlinedIcon sx={{fontSize: 15}} />} onClick={handleSubmit}>
              Submit for Prozo review
            </Button>
          )}
        </Box>
      </Box>
    </Drawer>
  )
}
